import React, { useState, useEffect, useRef, useCallback } from 'react';
import * as d3 from 'd3';
import { useApi } from '@bumble/app/hooks/use-api';
import { useQuery } from '@tanstack/react-query';
import DialogStandard from '../DialogStandard';
import { TraceRouteData } from './TraceRouteData';
import { TracerouteHop } from '@bumble/app/types/cpeTypes';

interface HopType {
  hop: number;
  hostname: string;
  ip: string;
  rtts: number[];
}

interface RouteData {
  destination: string;
  [key: string]: HopType[] | string;
  down_timestamp_utc: string;
  up_timestamp_utc: string;
}

interface PathVisualizationProps {
  nodeId : string;
  nodeName: string;
  tracerouteData: (data: TracerouteHop[]) => void;
}

const PathVisualization: React.FC<PathVisualizationProps> = ({ nodeId, nodeName, tracerouteData }) => {
  const [hoveredPath, setHoveredPath] = useState<string | null>(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const [showCurrentDialog, setShowCurrentDialog] = useState(false);
  const [showOutageDialog, setShowOutageDialog] = useState(false);
  const [currentHops, setCurrentHops] = useState<HopType[]>([]);
  const [outageHops, setOutageHops] = useState<HopType[]>([]);

  const containerRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const api = useApi();

  let isCalled = false;

  const { isLoading, refetch, data: responseData } = useQuery(
    ['Pathdata'],
    () => api.dashboardGetRouteApiV1DashboardGetRouteGet({
      node_id: nodeId,
      node_name: nodeName,
    }),
    { enabled: false }
  );

  const data = responseData?.data as RouteData;

  const updateDimensions = useCallback(() => {
    if (containerRef.current) {
      const { width } = containerRef.current.getBoundingClientRect();
      setDimensions({
        width: width,
        height: 350
      });
    }
  }, []);

  useEffect(() => {
    if (nodeName && nodeId && !isCalled) {
      refetch();
      isCalled = true;
    }
  }, [])

  useEffect(() => {
    updateDimensions();
    const resizeObserver = new ResizeObserver(updateDimensions);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }
    window.addEventListener('resize', updateDimensions);
    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateDimensions);
    };
  }, [updateDimensions]);

  useEffect(() => {
    if (!svgRef.current || isLoading || !data || dimensions.width === 0) return;
    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();
    const tooltip = d3.select('body')
      .append('div')
      .style('position', 'absolute')
      .style('background', 'rgba(0,0,0,0.8)')
      .style('color', 'white')
      .style('padding', '5px 10px')
      .style('border-radius', '5px')
      .style('font-size', '12px')
      .style('pointer-events', 'none')
      .style('opacity', 0);
    const margin = {
      top: Math.max(20, dimensions.height * 0.1),
      right: Math.max(20, dimensions.width * 0.05),
      bottom: Math.max(20, dimensions.height * 0.1),
      left: Math.max(20, dimensions.width * 0.05)
    };
    const width = dimensions.width - margin.left - margin.right;
    const height = dimensions.height - margin.top - margin.bottom;
    const g = svg.append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    const tracerouteList: TracerouteHop[] = [];
    const pathKeys = Object.keys(data).filter(
      key => key !== 'destination' && Array.isArray(data[key])
    );

    pathKeys.forEach(key => {
      const hops = data[key] as HopType[];

      if (Array.isArray(hops)) {
        hops.filter(hop =>
          !tracerouteList.some(item => item.hop === hop.hop && item.hostname === hop.hostname && item.ip === hop.ip)
        ).map(hop => ({ hop: hop.hop, hostname: hop.hostname, ip: hop.ip }));
      }
      tracerouteList.push(...hops);
    });
    tracerouteData(tracerouteList);

    if (!pathKeys.length) return;
    const pathSpacing = Math.min(100, height / (pathKeys.length + 1));
    const colorScale = d3.scaleOrdinal(d3.schemeCategory10);
    const maxHops = Math.max(...pathKeys.map(key => (data[key] as HopType[]).length), 1);
    const startX = Math.max(width * 0.05, 100);
    const endX = width * 0.85;
    const lastNodeX = startX + (maxHops - 1) * (endX - startX) / Math.max(maxHops - 1, 1);
    const paths = pathKeys.map((key, index) => {
      const hops = data[key] as HopType[];
      if (!hops.length) return null;
      const yOffset = (index - pathKeys.length / 2) * pathSpacing;
      const pathY = height / 2 + yOffset;
      return {
        name: key.replace('_', ' '),
        hops: hops,
        color: colorScale(String(index)),
        yOffset: yOffset,
        pathY: pathY
      };
    }).filter((path): path is NonNullable<typeof path> => path !== null);

    const currentPath = paths.find(p => p.name === 'up hops');
    const outagePath = paths.find(p => p.name === 'down hops');

    if (currentPath) setCurrentHops(currentPath.hops);
    if (outagePath) setOutageHops(outagePath.hops);

    g.selectAll('.toggle-button-bg')
      .data(paths)
      .enter()
      .append('rect')
      .attr('x', -45)
      .attr('y', d => d.pathY - 12)
      .attr('width', 50)
      .attr('height', 24)
      .attr('rx', 4)
      .attr('fill', 'white')
      .attr('stroke', 'black')
      .attr('stroke-width', 1.5)
      .attr('cursor', 'pointer')
      .on('click', (event, d) => {
        if (d.name === 'up hops') {
          setShowCurrentDialog(true);
        } else if (d.name === 'down hops') {
          setShowOutageDialog(true);
        }
      })
      .on('mouseover', function () {
        d3.select(this).attr('fill', '#f5f5f5');
      })
      .on('mouseout', function () {
        d3.select(this).attr('fill', 'white');
      });


    g.selectAll('.toggle-button-text')
      .data(paths)
      .enter()
      .append('text')
      .attr('x', -20)
      .attr('y', d => d.pathY + 5)
      .attr('fill', 'black')
      .attr('font-size', '14px')
      .attr('font-weight', 'bold')
      .attr('text-anchor', 'middle')
      .attr('pointer-events', 'none')
      .text('Show');

    if (!paths.length) return;
    const lastPath = paths.filter(path => path.hops.length > 0).slice(-1)[0]; // Last valid path
    const lastPathY = lastPath ? lastPath.pathY : height / 2;
    const destinationPoint = {
      id: 'Destination',
      hostname: data.destination,
      x: lastNodeX + width * 0.1,
      y: lastPathY - pathSpacing * (paths.length - 1) / 2,
      color: 'lightgreen',
    };
    let allNodes: any[] = [destinationPoint];
    let allLinks: any[] = [];
    let dashedLinks: any[] = [];
    paths.forEach((path) => {
      const pathY = height / 2 + path.yOffset;
      const hopSpacing = (lastNodeX - startX) / (maxHops -1);
      if (Array.isArray(path.hops)) {
        const pathNodes = path.hops.map((hop, i) => ({
          id: `${path.name}-${hop.hostname}`,
          hop: hop.hop,
          hostname: hop.hostname,
          ip: hop.ip,
          x: startX + i * hopSpacing,
          y: pathY,
          color: hop.hop === 1 ? 'green' : 'lightblue',
          data: hop,
          pathName: path.name,
        }));
        allNodes = [...allNodes, ...pathNodes];
        for (let i = 1; i < pathNodes.length; i++) {
          allLinks.push({
            source: pathNodes[i - 1],
            target: pathNodes[i],
            color: '#888',
            pathName: path.name,
          });
        }
        if (pathNodes.length) {
          const lastNode = pathNodes[pathNodes.length - 1];
          if (path.name !== "down hops" && lastNode.hostname !== data.destination) {
            dashedLinks.push({
              source: lastNode,
              target: destinationPoint,
              color: "#888",
              pathName: path.name,
            });
          }
        }
      }
    });
    g.selectAll('.solid-link')
      .data(allLinks)
      .enter()
      .append('line')
      .attr('class', 'solid-link')
      .attr('x1', d => d.source.x)
      .attr('y1', d => d.source.y)
      .attr('x2', d => d.target.x)
      .attr('y2', d => d.target.y)
      .attr('stroke', d => d.color)
      .attr('stroke-width', 2);
    g.selectAll('.dashed-link')
      .data(dashedLinks)
      .enter()
      .append('line')
      .attr('class', 'dashed-link')
      .attr('x1', d => d.source.x)
      .attr('y1', d => d.source.y)
      .attr('x2', d => d.target.x)
      .attr('y2', d => d.target.y)
      .attr('stroke', d => d.color)
      .attr('stroke-width', 2.5);
    g.append('circle')
      .attr('cx', destinationPoint.x)
      .attr('cy', destinationPoint.y)
      .attr('r', Math.min(15, width * 0.015))
      .attr('fill', 'none')
      .attr('stroke', '#ddd')
      .attr('stroke-width', 1)
      .attr('stroke-dasharray', '2,2')
      .attr('opacity', 0.7);
    const nodeRadius = Math.min(10, width * 0.01);
    const fontSize = Math.max(10, Math.min(12, width * 0.012));
    const nodeGroups = g.selectAll('.node')
      .data(allNodes)
      .enter()
      .append('g')
      .attr('class', 'node')
      .attr('transform', d => `translate(${d.x},${d.y})`)
      .on('mouseover', (event, d) => setHoveredPath(d.pathName))
      .on('mouseout', () => setHoveredPath(null));
    nodeGroups.append('circle')
      .attr('r', d => (d.id === 'Destination' ? 12 : 10))
      .attr('fill', d => d.color)
      .attr('stroke', '#000')
      .attr('stroke-width', 1.5)
      .on('mouseover', (event, d) => {
        tooltip.style('opacity', 1)
          .html(d.id !== 'Destination' ? `Hop: ${d.hop}<br>Hostname: ${d.hostname}<br>IP: ${d.ip}` : ``)
          .style('left', (event.pageX + 10) + 'px')
          .style('top', (event.pageY - 20) + 'px');
      })
      .on('mousemove', (event) => {
        tooltip.style('left', (event.pageX + 10) + 'px')
          .style('top', (event.pageY - 20) + 'px');
      })
      .on('mouseout', () => {
        tooltip.style('opacity', 0);
      });
    nodeGroups.append('text')
      .attr('dy', nodeRadius * 2.5)
      .attr('text-anchor', 'middle')
      .attr('font-size', `${fontSize}px`)
      .attr('font-weight', d => d.id === 'Destination' ? 'bold' : 'normal')
      .text(d => d.id === 'Destination' ?
        (width < 500 ? 'Destination' : `Destination: ${d.hostname}`) :
        d.hop);
    nodeGroups.append("text")
      .filter(d => d.hop === 1)
      .attr("dx", -20)
      .attr("dy", 5)
      .attr("text-anchor", "end")
      .attr("font-size", "12px")
      .attr("fill", "#333")
      .text(d => d.pathName === "up hops" ? "Current": "Last outage");
    g.selectAll('.solid-link')
      .transition()
      .duration(100)
      .attr('stroke', (d: any) => d.pathName === hoveredPath ? '#06BC42' : d.color);
    g.selectAll('.dashed-link')
      .transition()
      .duration(100)
      .attr('stroke', (d: any) => d.pathName === hoveredPath ? '#06BC42' : d.color);
    g.selectAll('.node circle')
      .transition()
      .duration(100)
      .attr('fill', (d: any) => d.pathName === hoveredPath ? '#06BC42' : d.color);
    return () => {
      tooltip.remove();
    }
  }, [data, isLoading, hoveredPath, dimensions]);

  return (
    <div ref={containerRef} className='w-full overflow-hidden'>
      <DialogStandard
        open={showCurrentDialog}
        handleClose={() => setShowCurrentDialog(false)}
        title="Traceroute Result"
      >
        <TraceRouteData
          onClose={() => setShowCurrentDialog(false)}
          onTracerouteData={currentHops.map(h => ({
            hop: h.hop,
            hostname: h.hostname,
            ip: h.ip
          } as TracerouteHop))}
          selectedTitle='Current'
          timestamp={data?.up_timestamp_utc}
        />
      </DialogStandard>
      <DialogStandard
        open={showOutageDialog}
        handleClose={() => setShowOutageDialog(false)}
        title="Traceroute Result"
      >
        <TraceRouteData
          selectedTitle='Last Outage'
          onClose={() => setShowOutageDialog(false)}
          onTracerouteData={outageHops.map(h => ({
            hop: h.hop,
            hostname: h.hostname,
            ip: h.ip
          } as TracerouteHop))}
          timestamp={data?.down_timestamp_utc}
        />
      </DialogStandard>
      <svg
        ref={svgRef}
        width={dimensions.width}
        height={dimensions.height}
        style={{ fontFamily: 'Arial, sans-serif' }}
        className='w-full'
      />
    </div>
  );
};

export default PathVisualization;
